import { useEffect } from 'react';
import { useDocsStore } from '../store/docsStore';
import { checkConnectivity } from '../utils/connectivity';

/**
 * Hook to monitor online/offline status
 * Updates the docsStore when connectivity changes
 */
export const useOnlineStatus = (): boolean => {
  const setOnlineStatus = useDocsStore((state) => state.setOnlineStatus);
  const isOnline = useDocsStore((state) => state.isOnline);

  useEffect(() => {
    const checkOnlineStatus = async () => {
      const isConnected = await checkConnectivity();
      setOnlineStatus(isConnected);
    };

    // Check on mount
    checkOnlineStatus();

    // Check periodically
    const interval = setInterval(checkOnlineStatus, 30000); // Every 30s

    // Also check on visibility change
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkOnlineStatus();
      }
    };

    // Handle online event - recheck actual connectivity
    const handleOnline = async () => {
      console.log('[useOnlineStatus] Network interface online - verifying backend...');
      const isConnected = await checkConnectivity();
      setOnlineStatus(isConnected);
    };

    // Handle offline event
    const handleOffline = () => {
      console.log('[useOnlineStatus] Network status: OFFLINE');
      setOnlineStatus(false);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [setOnlineStatus]);

  return isOnline;
};
